import { fetchAuthSession, getCurrentUser, signIn, signOut } from 'aws-amplify/auth';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';

/**
 * Web認証ユーザー情報
 */
export interface WebAuthUser {
  /** ユーザーID（sub） */
  userId: string;
  /** メールアドレス */
  email?: string;
  /** グループ（将来拡張用） */
  groups?: string[];
}

/**
 * Web認証状態
 */
export interface WebAuthState {
  /** 認証済みかどうか */
  isAuthenticated: boolean;
  /** ローディング中かどうか */
  isLoading: boolean;
  /** ユーザー情報 */
  user: WebAuthUser | null;
  /** エラーメッセージ */
  error: string | null;
}

/**
 * Web認証コンテキストの値
 */
export interface WebAuthContextValue extends WebAuthState {
  /** サインイン */
  signIn: (email: string, password: string) => Promise<void>;
  /** サインアウト */
  signOut: () => Promise<void>;
  /** 認証状態を再確認 */
  checkAuth: () => Promise<void>;
}

const WebAuthContext = createContext<WebAuthContextValue | null>(null);

/**
 * JWTトークンからユーザー情報を抽出
 */
function parseUserFromToken(idToken: string): WebAuthUser | null {
  try {
    const parts = idToken.split('.');
    if (parts.length !== 3 || !parts[1]) return null;

    const payload = JSON.parse(atob(parts[1]));
    const user: WebAuthUser = {
      userId: payload.sub,
      groups: payload['cognito:groups'] || [],
    };
    if (payload.email) {
      user.email = payload.email;
    }
    return user;
  } catch {
    return null;
  }
}

/**
 * Web認証コンテキストプロバイダー
 */
export function WebAuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<WebAuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    error: null,
  });

  /**
   * 認証状態を確認
   */
  const checkAuth = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      // 現在のユーザーを取得
      const currentUser = await getCurrentUser();

      // セッション（トークン）を取得
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        setState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          error: null,
        });
        return;
      }

      // トークンからユーザー情報を取得
      const user = parseUserFromToken(idToken);

      let finalUser = user;
      if (!finalUser) {
        const fallbackUser: WebAuthUser = {
          userId: currentUser.userId,
        };
        const loginEmail = currentUser.signInDetails?.loginId;
        if (loginEmail) {
          fallbackUser.email = loginEmail;
        }
        finalUser = fallbackUser;
      }
      setState({
        isAuthenticated: true,
        isLoading: false,
        user: finalUser,
        error: null,
      });
    } catch {
      // 未認証の場合
      setState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: null,
      });
    }
  }, []);

  /**
   * サインイン
   */
  const handleSignIn = useCallback(
    async (email: string, password: string) => {
      try {
        setState((prev) => ({ ...prev, isLoading: true, error: null }));

        const result = await signIn({ username: email, password });

        if (result.nextStep.signInStep === 'DONE') {
          // サインイン成功、認証状態を更新
          await checkAuth();
        } else {
          // 追加のステップが必要な場合
          setState((prev) => ({
            ...prev,
            isLoading: false,
            error: `追加の認証ステップが必要です: ${result.nextStep.signInStep}`,
          }));
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : '認証に失敗しました';
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: message,
        }));
        throw err;
      }
    },
    [checkAuth],
  );

  /**
   * サインアウト
   */
  const handleSignOut = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, isLoading: true }));
      await signOut();
      setState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: null,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'サインアウトに失敗しました';
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: message,
      }));
    }
  }, []);

  // 初期化時に認証状態を確認
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const value: WebAuthContextValue = {
    ...state,
    signIn: handleSignIn,
    signOut: handleSignOut,
    checkAuth,
  };

  return <WebAuthContext.Provider value={value}>{children}</WebAuthContext.Provider>;
}

/**
 * Web認証コンテキストを使用するフック
 */
export function useWebAuth(): WebAuthContextValue {
  const context = useContext(WebAuthContext);
  if (!context) {
    throw new Error('useWebAuth must be used within a WebAuthProvider');
  }
  return context;
}
